export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4
  label: 'Too Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong'
  color: string
  feedback: string[]
}

export function calculatePasswordStrength(password: string): PasswordStrength {
  let score = 0
  const feedback: string[] = []

  // Length check
  if (password.length >= 8) {
    score++
  } else {
    feedback.push('Use at least 8 characters')
  }

  if (password.length >= 12) {
    score++
  }

  // Lowercase letters
  if (/[a-z]/.test(password)) {
    score++
  } else {
    feedback.push('Include lowercase letters')
  }

  // Uppercase letters
  if (/[A-Z]/.test(password)) {
    score++
  } else {
    feedback.push('Include uppercase letters')
  }

  // Numbers
  if (/[0-9]/.test(password)) {
    score++
  } else {
    feedback.push('Include numbers')
  }

  // Special characters
  if (/[^A-Za-z0-9]/.test(password)) {
    score++
  } else {
    feedback.push('Include special characters (!@#$%^&*)')
  }

  // Common patterns (reduce score)
  const commonPatterns = [
    /^123/,
    /^abc/i,
    /password/i,
    /qwerty/i,
    /admin/i,
    /letmein/i,
  ]
  
  if (commonPatterns.some(pattern => pattern.test(password))) {
    score = Math.max(0, score - 2)
    feedback.push('Avoid common patterns')
  }

  // Calculate final score (0-4)
  const finalScore = Math.min(4, Math.max(0, score - 2)) as 0 | 1 | 2 | 3 | 4

  const labels: Record<number, PasswordStrength['label']> = {
    0: 'Too Weak',
    1: 'Weak',
    2: 'Fair',
    3: 'Good',
    4: 'Strong',
  }

  const colors = {
    0: 'bg-red-500',
    1: 'bg-orange-500',
    2: 'bg-yellow-500',
    3: 'bg-lime-500',
    4: 'bg-green-500',
  }

  return {
    score: finalScore,
    label: labels[finalScore],
    color: colors[finalScore],
    feedback: feedback.slice(0, 2), // Limit to 2 suggestions
  }
}

export function validateEmail(email: string): { isValid: boolean; message?: string } {
  if (!email) {
    return { isValid: false, message: 'Email is required' }
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address' }
  }

  return { isValid: true }
}

